import { useState, useEffect, useRef } from 'react';
import { LuSearch, LuPencil, LuUpload, LuTrash2 } from 'react-icons/lu';
import { Popover, message, Spin } from 'antd';
import { darkenHex } from './utils.js';
import { HERO_BG_FALLBACK } from './constants.js';
import { downloadFromS3Signed, uploadToS3 } from '../_shared/index.js';

function HeroSearch({
	accentColor,
	company,
	currentUser,
	onUploadImage,
	onRemoveImage,
	onSearch,
}) {
	const [query, setQuery] = useState('');
	const [signedUrl, setSignedUrl] = useState(null);
	const [imageReady, setImageReady] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const fileInputRef = useRef(null);

	const internalMobilityImage = company?.internalMobilityImage;
	const hasCustomImage = !!internalMobilityImage?.key;
	const isAdmin =
		currentUser?.displayAs !== 'employee' &&
		(currentUser?.role === 'admin' ||
			currentUser?.role === 'superAdmin' ||
			currentUser?.role === 'supportAdmin');

	useEffect(() => {
		let cancelled = false;
		if (!hasCustomImage) {
			setSignedUrl(null);
			setImageReady(true);
			return;
		}
		setImageReady(false);
		const fetchAndPreload = async () => {
			try {
				const url = await downloadFromS3Signed(
					internalMobilityImage.key,
					internalMobilityImage.bucket
				);
				if (cancelled || !url) {
					if (!cancelled) {
						setSignedUrl(null);
						setImageReady(true);
					}
					return;
				}
				const img = new Image();
				img.onload = () => {
					if (!cancelled) {
						setSignedUrl(url);
						setImageReady(true);
					}
				};
				img.onerror = () => {
					if (!cancelled) {
						setSignedUrl(null);
						setImageReady(true);
					}
				};
				img.src = url;
			} catch (error) {
				console.error('Error fetching hero image:', error);
				if (!cancelled) {
					setSignedUrl(null);
					setImageReady(true);
				}
			}
		};
		fetchAndPreload();
		return () => {
			cancelled = true;
		};
	}, [internalMobilityImage?.key, internalMobilityImage?.bucket, hasCustomImage]);

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!query.trim()) return;
		onSearch?.(query.trim());
	};

	const triggerFilePicker = () => {
		setMenuOpen(false);
		fileInputRef.current?.click();
	};

	const handleFileChange = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file || !company?.id) return;

		const newInternalMobilityImage = {
			bucket: 'erin-avatars',
			key: `internal-mobility/${company.id}/${file.name}`,
			region: 'us-east-2',
		};

		try {
			setUploading(true);
			await uploadToS3(
				file,
				newInternalMobilityImage.key,
				newInternalMobilityImage.bucket,
				file.type
			);
			await onUploadImage?.(newInternalMobilityImage);
			message.success('Hero image updated');
		} catch (error) {
			console.error('Error uploading hero image:', error);
			message.error('Failed to upload image');
		} finally {
			setUploading(false);
		}
	};

	const handleRemove = async () => {
		setMenuOpen(false);
		try {
			setUploading(true);
			await onRemoveImage?.();
			message.success('Hero image removed');
		} catch (error) {
			console.error('Error removing hero image:', error);
			message.error('Failed to remove image');
		} finally {
			setUploading(false);
		}
	};

	const bgImage = hasCustomImage && !imageReady
		? null
		: (signedUrl || HERO_BG_FALLBACK);

	const menuContent = (
		<div className="flex flex-col" style={{ minWidth: 160 }}>
			<button
				type="button"
				onClick={triggerFilePicker}
				className="flex items-center gap-2"
				style={{
					padding: '8px 12px',
					background: 'transparent',
					border: 'none',
					cursor: 'pointer',
					textAlign: 'left',
					fontSize: 13,
					color: '#1e293b',
					borderRadius: 6,
				}}
				onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
				onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
			>
				<LuUpload size={14} />
				{internalMobilityImage ? 'Replace image' : 'Upload image'}
			</button>
			{internalMobilityImage && (
				<button
					type="button"
					onClick={handleRemove}
					className="flex items-center gap-2"
					style={{
						padding: '8px 12px',
						background: 'transparent',
						border: 'none',
						cursor: 'pointer',
						textAlign: 'left',
						fontSize: 13,
						color: '#dc2626',
						borderRadius: 6,
					}}
					onMouseOver={(e) =>
						(e.currentTarget.style.background = 'rgba(220,38,38,0.08)')
					}
					onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
				>
					<LuTrash2 size={14} />
					Remove image
				</button>
			)}
		</div>
	);

	return (
		<div
			className="relative overflow-hidden full-bleed"
			style={{
				minHeight: 220,
				backgroundImage: bgImage
					? `linear-gradient(110deg, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.55) 55%, rgba(15,23,42,0.15) 100%), url(${bgImage})`
					: 'linear-gradient(110deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.85) 100%)',
				backgroundSize: bgImage ? 'cover, cover' : 'cover',
				backgroundPosition: 'center, center',
				backgroundRepeat: 'no-repeat, no-repeat',
				opacity: imageReady ? 1 : 0.6,
				transition: 'opacity 0.4s ease-out',
			}}
		>
			{isAdmin && (
				<div className="absolute z-10" style={{ top: 12, right: 12 }}>
					<Popover
						content={menuContent}
						trigger="click"
						placement="bottomRight"
						open={menuOpen}
						onOpenChange={setMenuOpen}
					>
						<button
							type="button"
							className="flex items-center gap-1.5 rounded-lg"
							disabled={uploading}
							style={{
								padding: '6px 12px',
								background: 'rgba(255,255,255,0.95)',
								color: '#1e293b',
								border: 'none',
								fontSize: 12,
								fontWeight: 600,
								cursor: uploading ? 'wait' : 'pointer',
								boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
								backdropFilter: 'blur(6px)',
							}}
						>
							{uploading ? (
								<Spin size="small" />
							) : (
								<>
									<LuPencil size={12} />
									Edit
								</>
							)}
						</button>
					</Popover>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*"
						onChange={handleFileChange}
						style={{ display: 'none' }}
					/>
				</div>
			)}

			<div
				className="flex flex-col justify-center"
				style={{ padding: '36px 32px', maxWidth: 640 }}
			>
				<h1 style={{ margin: 0, lineHeight: 1.15 }}>
					<span
						style={{
							display: 'block',
							color: 'rgba(255,255,255,0.9)',
							fontSize: 20,
							fontWeight: 500,
							letterSpacing: '0.01em',
						}}
					>
						Find Your Next Role at
					</span>
					<span
						style={{
							display: 'block',
							color: '#fff',
							fontSize: 32,
							fontWeight: 800,
							marginTop: 4,
						}}
					>
						{company?.name || 'Our Company'}
					</span>
				</h1>

				<form
					onSubmit={handleSubmit}
					className="mt-6 flex items-center rounded-xl"
					style={{ boxShadow: '0 6px 18px rgba(0,0,0,0.18)' }}
				>
					<div
						className="flex flex-1 items-center rounded-l-xl bg-white"
						style={{ overflow: 'hidden' }}
					>
						<div
							className="flex items-center justify-center"
							style={{ paddingLeft: 14, color: '#475569' }}
						>
							<LuSearch size={18} />
						</div>
						<input
							type="text"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search by skills, location, etc."
							className="flex-1 border-0 outline-none"
							style={{
								padding: '12px 14px',
								fontSize: 14,
								color: '#1e293b',
								background: 'transparent',
							}}
						/>
					</div>
					<button
						type="submit"
						style={{
							border: 'none',
							color: '#fff',
							background: accentColor,
							padding: '0 20px',
							alignSelf: 'stretch',
							borderRadius: '0 12px 12px 0',
							fontWeight: 600,
							fontSize: 14,
							cursor: 'pointer',
							transition: 'background 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.background = darkenHex(accentColor);
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.background = accentColor;
						}}
					>
						<LuSearch size={18} />
					</button>
				</form>
			</div>
		</div>
	);
}

export default HeroSearch;
